import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnDestroy, OnInit, signal, ViewChild, inject } from "@angular/core";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { Utilities } from "@tylertech/shared-module";
import { IChartCriteria, ProfileSettingViewModel } from "@tylertech/tyler-api-types";
import { ChartConfiguration, ChartEvent, LegendElement, LegendItem, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "utilities-prepaid-billing-chart",
    templateUrl: "./prepaidBillingChart.tmpl.html",
    styleUrls: [
        "./prepaidBillingChart.component.scss"
    ],
    standalone: false
})
export class PrepaidBillingChartComponent implements OnDestroy, OnInit, ICustomViewComponent<ChartBindingObject>
{
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $chartService = inject(ChartService);

    private readonly destructor$ = new Subject<void>();

    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;

    private _chartCriteria: IChartCriteria;
    readonly customDataBinding: ChartBindingObject;

    isLoaded: boolean;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise(
            () =>
            {
                if (!this.hasData())
                {
                    return this.loadChartData();
                }
                this.updateChartConfiguration();
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.hasData())
                    {
                        return;
                    }
                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private updateChartConfiguration(): void
    {
        const chartColors = this.$chartService.getColors(1);
        this.chartConfig = {
            type: "bar",
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12 },
                        onClick: (_event: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const ci = legend.chart;
                            const meta = ci.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = !meta.hidden ? !ci.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (ci.data.datasets || []).length; ++i)
                            {
                                const iMeta = ci.getDatasetMeta(i);
                                if (!iMeta.hidden && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            ci.options.scales[yAxisID].display = showScale;
                            ci.update();
                        },
                    },
                },
                elements: { line: {} },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    y: {
                        type: "linear",
                        display: true,
                        position: "left",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(
                                    value > -10 && value < 10
                                        ? value.toFixed(2)
                                        : value.toFixed(0));
                            },
                        },
                    },
                },
            },
            data: {
                datasets: this.chartCriteria.ChartDatas.map(chartData => ({
                    type: "bar",
                    label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                    data: this.chartCriteria.ChartDatas[0].Data,
                    backgroundColor: chartColors[0],
                    borderColor: chartColors[0],
                    hoverBackgroundColor: chartColors[0],
                    hoverBorderColor: chartColors[0],
                    categoryPercentage: 0.5,
                    barPercentage: 0.8,
                })),
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }

    displayChartParameterDialogAndReload(): void
    {
        this.$chartDialogService.show(this.chartCriteria.Parameters, "Prepaid Billing")
            .then((result: ProfileSettingViewModel[]) =>
            {
                if (!Utilities.arraysEqual(this.chartCriteria.Parameters, result))
                {
                    this.chartCriteria.Parameters = result;
                    return this.loadChartData();
                }
            });
    }
}
